import { db } from '../lib/firebase';
import { 
  collection, 
  doc, 
  setDoc, 
  deleteDoc, 
  getDoc,
  serverTimestamp,
  query,
  where,
  getDocs
} from 'firebase/firestore';

// Database structure: vindie/{collection}

export interface Subscription {
  id?: string;
  subscriberId: string;
  creatorId: string;
  createdAt: any;
}

export async function followCreator(subscriberId: string, creatorId: string): Promise<void> {
  // Get creator profile data
  const creatorRef = doc(db, 'users', creatorId);
  const creatorDoc = await getDoc(creatorRef);
  const creatorData = creatorDoc.data();
  
  // Write to user's subscriptions subcollection (for real-time updates)
  const userSubscriptionRef = doc(db, 'users', subscriberId, 'subscriptions', creatorId);
  await setDoc(userSubscriptionRef, {
    id: creatorId,
    name: creatorData?.name || creatorData?.username || 'Unknown Creator',
    avatar: creatorData?.avatarUrl || '',
    followers: 0,
    verified: false,
    isOnline: false,
    lastActive: serverTimestamp(),
    newContentCount: 0,
    subscribedAt: serverTimestamp()
  });

  // Also keep top-level subscription for querying
  const subscriptionId = `${subscriberId}_${creatorId}`;
  const ref = doc(db, 'subscriptions', subscriptionId);
  await setDoc(ref, {
    subscriberId,
    creatorId,
    createdAt: serverTimestamp()
  });
}

export async function unfollowCreator(subscriberId: string, creatorId: string): Promise<void> {
  // Delete from user's subscriptions subcollection
  const userSubscriptionRef = doc(db, 'users', subscriberId, 'subscriptions', creatorId);
  await deleteDoc(userSubscriptionRef);

  // Delete from top-level subscriptions
  const subscriptionId = `${subscriberId}_${creatorId}`;
  const ref = doc(db, 'subscriptions', subscriptionId);
  await deleteDoc(ref);
}

export async function isFollowing(subscriberId: string, creatorId: string): Promise<boolean> {
  try {
    // Check user's subscriptions subcollection first
    const userSubscriptionRef = doc(db, 'users', subscriberId, 'subscriptions', creatorId);
    const userSubscriptionDoc = await getDoc(userSubscriptionRef);
    
    if (userSubscriptionDoc.exists()) {
      return true;
    }
    
    // Fallback to top-level subscription check
    const subscriptionId = `${subscriberId}_${creatorId}`;
    const ref = doc(db, 'subscriptions', subscriptionId);
    const snap = await getDoc(ref);
    
    return snap.exists();
  } catch (error) {
    console.error('Error checking follow status:', error);
    return false;
  }
}

// Legacy function names for backward compatibility
export async function subscribeToCreator(subscriberId: string, creatorId: string): Promise<void> {
  return followCreator(subscriberId, creatorId);
}

export async function unsubscribeFromCreator(subscriberId: string, creatorId: string): Promise<void> {
  return unfollowCreator(subscriberId, creatorId);
}

export async function isSubscribed(subscriberId: string, creatorId: string): Promise<boolean> {
  return isFollowing(subscriberId, creatorId);
}

export async function getSubscriptions(subscriberId: string): Promise<Subscription[]> {
  const q = query(
    collection(db, 'subscriptions'),
    where('subscriberId', '==', subscriberId)
  );
  
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as Subscription));
}

export async function getSubscribers(creatorId: string): Promise<Subscription[]> {
  const q = query(
    collection(db, 'subscriptions'),
    where('creatorId', '==', creatorId)
  );
  
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as Subscription));
}

export async function getSubscriptionCount(creatorId: string): Promise<number> {
  const q = query(
    collection(db, 'subscriptions'),
    where('creatorId', '==', creatorId)
  );
  
  const snap = await getDocs(q);
  return snap.size;
}

